// Allowed `:module` values (the URL slug for each ISCC scheme).
export const MODULES = Object.freeze({
  isccEu: 'iscc-eu',
  isccPlus: 'iscc-plus',
  isccCorsia: 'iscc-corsia',
});
export const DEFAULT_MODULE = MODULES.isccEu;

export const MODULE_LABELS = Object.freeze({
  [MODULES.isccEu]: 'ISCC EU',
  [MODULES.isccPlus]: 'ISCC PLUS',
  [MODULES.isccCorsia]: 'ISCC CORSIA',
});

// The server's `certificate_type` enum for each module slug — what scopes account.
export const MODULE_CERTIFICATE_TYPES = Object.freeze({
  [MODULES.isccEu]: 'ISCC_EU',
  [MODULES.isccPlus]: 'ISCC_PLUS',
  [MODULES.isccCorsia]: 'ISCC_CORSIA',
});

export const SEGMENTS = Object.freeze({
  module: ':module',
  inbounds: 'inbounds',
  outbounds: 'outbounds',
  monitor: 'monitor',
  account: 'account',
});

export const ACCOUNT_SECTION_PARAM = 'section';
export const ACCOUNT_SECTIONS = Object.freeze({
  companyProfile: 'companyProfile',
  companyStructure: 'companyStructure',
  massBalancePeriod: 'massBalancePeriod',
  materials: 'materials',
  systemSettings: 'systemSettings',
});
export const DEFAULT_ACCOUNT_SECTION = ACCOUNT_SECTIONS.companyProfile;

export const CUSTOMER_TAB_PARAM = 'tab';
export const CUSTOMER_TABS = Object.freeze({
  sites: 'sites',
  transportation: 'transportation',
});
export const DEFAULT_CUSTOMER_TAB = CUSTOMER_TABS.sites;

export const ROUTES = {
  login: '/login',
  signup: '/signup',
  forgotPassword: '/forgot-password',
  resetPassword: '/resetPassword/:userId/:token',
  verifyCode: '/verify',

  root: '/',
  home: '/',
  profile: '/profile',
  admin: '/admin',
  activityLog: '/admin/activity',
  // Account setup is account-wide, like admin and the networks — never module-scoped.
  accountSetup: (section = DEFAULT_ACCOUNT_SECTION) =>
    `/${SEGMENTS.account}?${ACCOUNT_SECTION_PARAM}=${section}`,
  siteCreate: () => `/${SEGMENTS.account}/sites/new`,
  siteDetail: (id) => `/${SEGMENTS.account}/sites/${id}`,
  siteEdit: (id) => `/${SEGMENTS.account}/sites/${id}/edit`,

  customers: '/customers',
  customerCreate: '/customers/new',
  customerDetail: (id, tab = DEFAULT_CUSTOMER_TAB) =>
    `/customers/${id}?${CUSTOMER_TAB_PARAM}=${tab}`,
  customerSitesCreate: (id) => `/customers/${id}/sites/new`,
  customerSiteEdit: (id, siteId) => `/customers/${id}/sites/${siteId}/edit`,
  suppliers: '/suppliers',
  supplierDetail: (id) => `/suppliers/${id}`,

  contracts: (m) => `/${m}/inbounds/contracts`,
  contractCreate: (m) => `/${m}/inbounds/contracts/new`,
  contractDetail: (m, id) => `/${m}/inbounds/contracts/${id}`,
  contractEdit: (m, id) => `/${m}/inbounds/contracts/${id}/edit`,
  contractReceiveShipment: (m, id) => `/${m}/inbounds/contracts/${id}/receive-shipment`,
  incomingShipments: (m) => `/${m}/inbounds/incoming-shipments`,
  // TODO: incomingShipmentLinkContracts — the Link contracts screen isn't built yet.
  incomingShipmentCreate: (m) => `/${m}/inbounds/incoming-shipments/new`,
  incomingShipmentEdit: (m, id) => `/${m}/inbounds/incoming-shipments/${id}/edit`,
  incomingShipmentDetail: (m, id) => `/${m}/inbounds/incoming-shipments/${id}`,
  incomingShipmentAddDeclarations: (m, id) =>
    `/${m}/inbounds/incoming-shipments/${id}/add-declarations`,
  declarations: (m) => `/${m}/inbounds/declarations`,
  declarationDetail: (m, id) => `/${m}/inbounds/declarations/${id}`,
  validateDeclaration: (m, id) => `/${m}/inbounds/declarations/${id}/validate`,
  declarationEdit: (m, id) => `/${m}/inbounds/declarations/${id}/edit`,
  declarationCreate: (m) => `/${m}/inbounds/declarations/new`,

  orders: (m) => `/${m}/outbounds/orders`,
  orderCreate: (m) => `/${m}/outbounds/orders/new`,
  orderEdit: (m, id) => `/${m}/outbounds/orders/${id}/edit`,
  orderDetail: (m, id) => `/${m}/outbounds/orders/${id}`,
  orderFulfill: (m, id) => `/${m}/outbounds/orders/${id}/fulfill`,
  outgoingShipments: (m) => `/${m}/outbounds/outgoing-shipments`,
  outgoingShipmentCreate: (m) => `/${m}/outbounds/outgoing-shipments/new`,
  outgoingShipmentEdit: (m, id) => `/${m}/outbounds/outgoing-shipments/${id}/edit`,
  outgoingShipmentDetail: (m, id) => `/${m}/outbounds/outgoing-shipments/${id}`,
  outgoingShipmentFulfill: (m, id) => `/${m}/outbounds/outgoing-shipments/${id}/fulfill`,
  allocations: (m) => `/${m}/outbounds/allocations`,
  allocationDetail: (m, id) => `/${m}/outbounds/allocations/${id}`,
  allocationEditDeclaration: (m, id) => `/${m}/outbounds/allocations/${id}/edit-declaration`,
  // After a fulfil: one new allocation opens its declaration form; several, the Allocations list.
  generateDeclarationFor: (m, allocationIds = []) =>
    allocationIds.length === 1
      ? `/${m}/outbounds/allocations/${allocationIds[0]}/edit-declaration`
      : `/${m}/outbounds/allocations`,

  inventory: (m) => `/${m}/monitor/inventory`,
  inventoryDetail: (m, id) => `/${m}/monitor/inventory/${id}`,
  transactions: (m) => `/${m}/monitor/transactions`,

  notFound: '*',
};
